import i18n from '~/i18n';
import { WalletOutlined } from '@ant-design/icons';
import useOpenPaymentPanel from '../PaymentPanel';
import useOpenPaymentDonePanel from '../PaymentDonePanel';
import { PaymentMethod } from '~/types/Payment';
import { useCallback, useContext, useState } from 'react';
import PageContext from '../../context';
import { payOrder } from '../../../../api';
import message from '~/utils/message';
import { Order } from '@restaurant/api';
import { DrawerContext } from '~/providers/DrawerProvider';
import { Button, InputNumber, Radio } from 'antd';
import { formatCurrency } from '@restaurant/utils';

type SubmitCallback = (data: {
  isPartial: boolean;
  value: number | null;
}) => void;

const PARTIAL_PAYMENT_OPTIONS = [
  { value: false, label: 'Bayar Lunas' },
  { value: true, label: 'Bayar Sebagian' },
];

const PartialPaymentForm = ({
  outstandingPayment,
  onSubmit,
}: {
  outstandingPayment: number;
  onSubmit: SubmitCallback;
}) => {
  const [isPartial, setIsPartial] = useState(false);
  const [paymentValue, setPaymentValue] = useState<number | null>(null);
  const handleSubmit = () => {
    if (isPartial && !paymentValue) {
      message.error('Isi jumlah pembayaran');
      return;
    }
    onSubmit({
      isPartial,
      value: paymentValue,
    });
  };
  return (
    <div className="flex flex-col gap-6">
      <div>
        <p>Tagihan</p>
        <p className="font-medium text-2xl">
          {formatCurrency(outstandingPayment)}
        </p>
      </div>

      <div className="flex items-center gap-4">
        <Radio.Group
          options={PARTIAL_PAYMENT_OPTIONS}
          value={isPartial}
          onChange={(e) => setIsPartial(e.target.value)}
          optionType="button"
          buttonStyle="solid"
        />
      </div>
      {isPartial ? (
        <div>
          <p className="mb-2">Jumlah</p>
          <InputNumber
            value={paymentValue}
            min={1000}
            max={outstandingPayment}
            onChange={(value) => setPaymentValue(value)}
            className="max-w-[600px] w-full"
          />
        </div>
      ) : null}
      <Button type="primary" onClick={handleSubmit} size="large">
        Submit
      </Button>
    </div>
  );
};

const useOpenPartialPaymentPanel = () => {
  const { openDrawer } = useContext(DrawerContext);
  return useCallback(
    ({
      outstandingPayment,
      onSubmit,
    }: {
      outstandingPayment: number;
      onSubmit: SubmitCallback;
    }) => {
      openDrawer(
        ({ closeDrawer }) => {
          const handleSubmit: SubmitCallback = (params) => {
            closeDrawer();
            onSubmit(params);
          };
          return (
            <PartialPaymentForm
              outstandingPayment={outstandingPayment}
              onSubmit={handleSubmit}
            />
          );
        },
        { placement: 'bottom' }
      );
    },
    []
  );
};

const useIndentPayButton = ({ order }: { order: Order }) => {
  const { refresh } = useContext(PageContext);
  const openPartialPaymentPanel = useOpenPartialPaymentPanel();
  const openPaymentPanel = useOpenPaymentPanel();
  const openPaymentDonePanel = useOpenPaymentDonePanel();

  return {
    key: 'pay',
    text: i18n.t('cart_pay'),
    icon: <WalletOutlined />,
    onClick: async () => {
      if (
        !order.batches.every((items) =>
          items.every(({ isChecked }) => isChecked)
        )
      ) {
        message.error(
          'Order hanya bisa dibayar bila semua item sudah terceklis!'
        );
        return;
      }

      const outstandingPayment =
        order.totalPrice -
        (order.payment
          ? order.payment.totalAmount + order.payment.roundingAmount
          : 0);
      if (outstandingPayment <= 0) {
        message.error('Tagihan sudah lunas!');
        return;
      }

      const { value, isPartial } = await new Promise<
        Parameters<SubmitCallback>[0]
      >((res) => {
        openPartialPaymentPanel({
          outstandingPayment,
          onSubmit: (data) => res(data),
        });
      });

      const totalPrice = isPartial ? value! : outstandingPayment;

      openPaymentPanel({
        totalPrice,
        disableRounding: isPartial,
        onSubmit: async ({ roundingAmount, payments }) => {
          const totalPaymentAmount = payments.reduce((acc, { amount }) => {
            acc += amount;
            return acc;
          }, 0);

          const paymentProcessed = payments.map((p) => {
            if (p.method !== PaymentMethod.Cash) return p;

            /**
             * Example:
             * Total Price 500.0000
             * Total Payment 600.000 (Raw cash 120.000, rest 480.000)
             * -------------
             * Change = Total Payment - Total Price = 600.000 - 500.000 = 100.000
             * Actual Cash Paid = Raw Cash - Change = 120.000 - 100.000 = 20.000
             */
            const change = totalPaymentAmount - (totalPrice - roundingAmount);
            const amount = p.amount - change;

            return {
              method: p.method,
              description: p.description,
              amount,
              change,
              cash: p.amount,
            };
          });

          try {
            await payOrder(order.orderId, {
              roundingAmount,
              payments: paymentProcessed,
            });
            openPaymentDonePanel({
              order,
              totalPrice,
              roundingAmount,
              payments,
              onSubmit: () => {
                refresh();
              },
            });
          } catch (e: any) {
            message.error('Gagal bayar:' + e.message);
          }
        },
      });
    },
  };
};

export default useIndentPayButton;
